import { Box, Button, Center, Group, Loader, Paper, Text, TextInput, Tooltip } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { CreateFamilyPayload } from 'src/clients/hooks/useCreateFamily';
import { Family, useFamily } from 'src/clients/hooks/useFamily';
import { useUpdateFamily } from 'src/clients/hooks/useUpdateFamily';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SquareIconButton } from '../SquareIconButton';
import { ChangeFamilyAdvisorModal } from './ChangeFamilyAdvisorModal';
import { FamilyEmptyState } from './FamilyEmptyState';
import { FamilyMembersTable } from './FamilyMembersTable';

export function FamilyProfileContent() {
  const { client, isLoading } = useCurrentClient();

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  if (!client) {
    return null;
  }

  if (client.familyId) {
    return <Navigate to={`/family/${client.familyId}/profile?member=${client.id}`} replace />;
  }

  return (
    <Paper variant="content">
      <FamilyEmptyState firstName={client.firstName} />
    </Paper>
  );
}

function buildRenamePayload(family: Family, name: string): CreateFamilyPayload {
  return {
    name,
    advisorUserId: family.advisorUserId,
    mainFamilyMember: { clientId: family.mainFamilyMember.clientId },
    secondaryFamilyMembers: family.secondaryFamilyMembers.map(member => ({
      clientId: member.clientId,
      relationshipToMainFamilyMember: member.relationshipToMainFamilyMember,
    })),
  };
}

export function FamilyProfilePanel({ familyId }: { familyId: string }) {
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { updateFamily, isLoading: isRenaming, error: renameError, reset } = useUpdateFamily();
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [isChangingAdvisor, setIsChangingAdvisor] = useState(false);

  if (isFamilyLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  if (!family) {
    return (
      <Paper variant="content">
        <Text c="gray.6">This family could not be found.</Text>
      </Paper>
    );
  }

  function startEditing() {
    reset();
    setNameValue(family?.name ?? '');
    setIsEditingName(true);
  }

  async function confirmRename() {
    const trimmed = nameValue.trim();
    if (!trimmed || !family) return;
    try {
      // The update endpoint replaces the full family, so we resend the existing
      // members/relationships unchanged and only swap the name (omitting them would wipe them).
      await updateFamily(familyId, buildRenamePayload(family, trimmed));
      setIsEditingName(false);
    } catch {
      // error surfaced via renameError
    }
  }

  return (
    <Paper variant="content">
      <Group justify="space-between" align="center" mb="lg">
        <Group gap="xs" align="center">
          {isEditingName ? (
            <>
              <TextInput
                value={nameValue}
                onChange={event => setNameValue(event.currentTarget.value)}
                aria-label="Family name"
                size="sm"
                error={renameError ? 'Could not rename the family. Please try again.' : undefined}
                autoFocus
              />
              <Button
                variant="primary"
                size="sm"
                onClick={confirmRename}
                loading={isRenaming}
                disabled={!nameValue.trim()}
              >
                Confirm
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setIsEditingName(false)}
                disabled={isRenaming}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Text fw={FONT_WEIGHTS.semibold} size="lg" c="gray.10">
                {family.name}
              </Text>
              <SquareIconButton
                diameter={32}
                radius={8}
                aria-label="Edit family name"
                onClick={startEditing}
              >
                <IconPencil size={16} color="var(--mantine-color-gray-8)" />
              </SquareIconButton>
            </>
          )}
        </Group>
        <Group gap="xs">
          <Tooltip label="Coming soon" withArrow>
            <Box>
              <Button variant="primary" disabled size="sm">
                + Add family member
              </Button>
            </Box>
          </Tooltip>
          <Button variant="default" size="sm" onClick={() => setIsChangingAdvisor(true)}>
            Change advisor
          </Button>
        </Group>
      </Group>
      <FamilyMembersTable familyId={familyId} />
      <ChangeFamilyAdvisorModal
        familyId={familyId}
        currentAdvisorId={family.advisorUserId}
        opened={isChangingAdvisor}
        onClose={() => setIsChangingAdvisor(false)}
      />
    </Paper>
  );
}
